import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import QuickBooksService, {
  type QuickBooksAccount,
  type QuickBooksConnection,
  type QuickBooksTransaction,
} from '@/services/QuickBooksService';
import { AssetService } from '@/services/AssetService';
import type { Asset } from '@/types/asset';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { QuickBooksFeature } from '@/components/FeatureGate';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CheckCircle2,
  ExternalLink,
  Loader2,
  PlusCircle,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

function QuickBooksPageContent() {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [syncingAccountId, setSyncingAccountId] = useState<number | null>(null);
  const [syncingTransactionsId, setSyncingTransactionsId] = useState<number | null>(null);
  const [selectedConnection, setSelectedConnection] =
    useState<QuickBooksConnection | null>(null);
  const [showAccountsDialog, setShowAccountsDialog] = useState(false);
  const [showTransactionsDialog, setShowTransactionsDialog] = useState(false);
  const [mappingTransaction, setMappingTransaction] = useState<QuickBooksTransaction | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);

  // Fetch connections
  const { data: connections, isLoading } = useQuery({
    queryKey: ['quickbooks-connections'],
    queryFn: () => QuickBooksService.getConnections(),
  });

  // Fetch accounts for selected connection
  const { data: accounts } = useQuery({
    queryKey: ['quickbooks-accounts', selectedConnection?.id],
    queryFn: () => {
      if (!selectedConnection?.id) {
        throw new Error('No connection selected');
      }
      return QuickBooksService.getAccounts(selectedConnection.id);
    },
    enabled: !!selectedConnection?.id,
  });

  // Fetch transactions for selected connection
  const { data: transactions } = useQuery({
    queryKey: ['quickbooks-transactions', selectedConnection?.id],
    queryFn: () => {
      if (!selectedConnection?.id) {
        throw new Error('No connection selected');
      }
      return QuickBooksService.getTransactions(selectedConnection.id);
    },
    enabled: !!selectedConnection?.id && showTransactionsDialog,
  });

  // Fetch assets for mapping
  useEffect(() => {
    if (mappingTransaction) {
      AssetService.getAssets()
        .then(setAssets)
        .catch(() => setAssets([]));
    }
  }, [mappingTransaction]);

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: async ({
      authCode,
      realmId,
    }: {
      authCode: string;
      realmId: string;
    }) => {
      return QuickBooksService.connectQuickBooks({ authCode, realmId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quickbooks-connections'] });
      setConnecting(false);
      toast.success('QuickBooks connected successfully');
    },
    onError: (error: any) => {
      console.error('Error connecting QuickBooks:', error);
      toast.error(
        error?.response?.data?.error || 'Failed to connect QuickBooks',
      );
      setConnecting(false);
    },
  });

  // Sync accounts mutation
  const syncAccountsMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      return QuickBooksService.syncAccounts(connectionId);
    },
    onSuccess: (data, connectionId) => {
      queryClient.invalidateQueries({
        queryKey: ['quickbooks-accounts', connectionId],
      });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-connections'] });
      toast.success(`Synced ${data.count} accounts successfully`);
      setSyncingAccountId(null);
    },
    onError: (error: any) => {
      console.error('Error syncing accounts:', error);
      toast.error(
        error?.response?.data?.error || 'Failed to sync accounts',
      );
      setSyncingAccountId(null);
    },
  });

  // Sync transactions mutation
  const syncTransactionsMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      return QuickBooksService.syncTransactions(connectionId);
    },
    onSuccess: (data, connectionId) => {
      queryClient.invalidateQueries({
        queryKey: ['quickbooks-transactions', connectionId],
      });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-connections'] });
      toast.success(`Synced ${data.count} transactions successfully`);
      setSyncingTransactionsId(null);
    },
    onError: (error: any) => {
      console.error('Error syncing transactions:', error);
      toast.error(
        error?.response?.data?.error || 'Failed to sync transactions',
      );
      setSyncingTransactionsId(null);
    },
  });

  // Map transaction mutation
  const mapTransactionMutation = useMutation({
    mutationFn: async ({
      connectionId,
      transactionId,
      assetId,
      category,
      unit,
    }: {
      connectionId: number;
      transactionId: string;
      assetId: string;
      category?: string;
      unit?: number | null;
    }) => {
      return QuickBooksService.mapTransactionToAsset(connectionId, transactionId, {
        assetId,
        category,
        unit,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['quickbooks-transactions', variables.connectionId],
      });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Transaction mapped to asset successfully');
      setMappingTransaction(null);
    },
    onError: (error: any) => {
      console.error('Error mapping transaction:', error);
      toast.error(
        error?.response?.data?.error || 'Failed to map transaction to asset',
      );
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      return QuickBooksService.disconnectQuickBooks(connectionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quickbooks-connections'] });
      if (selectedConnection) {
        queryClient.removeQueries({
          queryKey: ['quickbooks-accounts', selectedConnection.id],
        });
        setSelectedConnection(null);
        setShowAccountsDialog(false);
      }
      toast.success('QuickBooks disconnected successfully');
    },
    onError: (error: any) => {
      console.error('Error disconnecting QuickBooks:', error);
      toast.error(
        error?.response?.data?.error || 'Failed to disconnect QuickBooks',
      );
    },
  });

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const realmId = urlParams.get('realmId');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      // Check if this is a QuickBooks callback
      if (code || realmId || error) {
        // Remove query params from URL
        window.history.replaceState({}, document.title, window.location.pathname);

        if (error) {
          toast.error(`QuickBooks authorization failed: ${error}`);
          setConnecting(false);
          return;
        }

        if (code && realmId) {
          // Verify state token (stored in sessionStorage)
          const storedState = sessionStorage.getItem('qb_oauth_state');
          if (state && storedState && state !== storedState) {
            toast.error('Security error: State token mismatch');
            setConnecting(false);
            return;
          }

          setConnecting(true);
          connectMutation.mutate({ authCode: code, realmId });
          sessionStorage.removeItem('qb_oauth_state');
        }
      }
    };

    handleOAuthCallback();
  }, [connectMutation]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const { auth_url, state } = await QuickBooksService.getAuthUrl();

      // Store state in sessionStorage for verification
      sessionStorage.setItem('qb_oauth_state', state);

      // Redirect to QuickBooks
      window.location.href = auth_url;
    } catch (error: any) {
      console.error('Error initiating QuickBooks connection:', error);
      toast.error(
        error?.response?.data?.error ||
          'Failed to initialize QuickBooks connection',
      );
      setConnecting(false);
    }
  };

  const handleSyncAccounts = async (connectionId: number) => {
    setSyncingAccountId(connectionId);
    syncAccountsMutation.mutate(connectionId);
  };

  const handleDisconnect = async (connectionId: number) => {
    if (
      window.confirm(
        'Are you sure you want to disconnect this QuickBooks company? This will remove all synced data.',
      )
    ) {
      disconnectMutation.mutate(connectionId);
    }
  };

  const handleViewAccounts = async (connection: QuickBooksConnection) => {
    setSelectedConnection(connection);
    setShowAccountsDialog(true);
  };

  const handleViewTransactions = async (connection: QuickBooksConnection) => {
    setSelectedConnection(connection);
    setShowTransactionsDialog(true);
  };

  const handleSyncTransactions = async (connectionId: number) => {
    setSyncingTransactionsId(connectionId);
    syncTransactionsMutation.mutate(connectionId);
  };

  const handleMapTransaction = (transaction: QuickBooksTransaction) => {
    setMappingTransaction(transaction);
  };

  const handleMapSubmit = (assetId: string, category?: string, unit?: number | null) => {
    if (!selectedConnection || !mappingTransaction) return;

    mapTransactionMutation.mutate({
      connectionId: selectedConnection.id,
      transactionId: mappingTransaction.id,
      assetId,
      category,
      unit,
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">QuickBooks Integration</h1>
          <p className="text-muted-foreground mt-2">
            Connect your QuickBooks companies to sync accounts and transactions
          </p>
        </div>
        <Button
          onClick={handleConnect}
          disabled={connecting}
          className="gap-2"
        >
          {connecting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <PlusCircle className="h-4 w-4" />
              Connect QuickBooks
            </>
          )}
        </Button>
      </div>

      {!connections || connections.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No QuickBooks Connections
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              Connect your QuickBooks company to start syncing accounts and
              transactions
            </p>
            <Button onClick={handleConnect} disabled={connecting}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Connect QuickBooks
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {connections.map((connection) => (
            <Card key={connection.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">
                      {connection.company_name}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    {connection.status === 'active' ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  <p>Company ID: {connection.realm_id}</p>
                  {connection.last_synced_at && (
                    <p>
                      Last synced:{' '}
                      {new Date(connection.last_synced_at).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewAccounts(connection)}
                      className="flex-1"
                    >
                      View Accounts
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSyncAccounts(connection.id)}
                      disabled={syncingAccountId === connection.id}
                    >
                      {syncingAccountId === connection.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewTransactions(connection)}
                      className="flex-1"
                    >
                      View Transactions
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSyncTransactions(connection.id)}
                      disabled={syncingTransactionsId === connection.id}
                    >
                      {syncingTransactionsId === connection.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDisconnect(connection.id)}
                    disabled={disconnectMutation.isPending}
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Disconnect
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Accounts Dialog */}
      <Dialog open={showAccountsDialog} onOpenChange={setShowAccountsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Accounts - {selectedConnection?.company_name}
            </DialogTitle>
            <DialogDescription>
              QuickBooks chart of accounts for this company
            </DialogDescription>
          </DialogHeader>

          {accounts && accounts.length > 0 ? (
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Sub Type</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-medium">
                        {account.name}
                      </TableCell>
                      <TableCell>{account.account_type}</TableCell>
                      <TableCell>{account.account_sub_type}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(account.balance, account.currency)}
                      </TableCell>
                      <TableCell>
                        {account.active ? (
                          <span className="text-green-600">Active</span>
                        ) : (
                          <span className="text-gray-500">Inactive</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="mt-4 text-center py-8 text-muted-foreground">
              <p>No accounts synced yet.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() =>
                  selectedConnection &&
                  handleSyncAccounts(selectedConnection.id)
                }
                disabled={
                  !selectedConnection ||
                  syncingAccountId === selectedConnection.id
                }
              >
                {syncingAccountId === selectedConnection?.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Accounts
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transactions Dialog */}
      <Dialog open={showTransactionsDialog} onOpenChange={setShowTransactionsDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Transactions - {selectedConnection?.company_name}
            </DialogTitle>
            <DialogDescription>
              QuickBooks transactions. Map them to your assets to import into BetterCapital.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex justify-end mb-4">
            <Button
              variant="outline"
              onClick={() =>
                selectedConnection && handleSyncTransactions(selectedConnection.id)
              }
              disabled={
                !selectedConnection ||
                syncingTransactionsId === selectedConnection.id
              }
            >
              {syncingTransactionsId === selectedConnection?.id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Transactions
                </>
              )}
            </Button>
          </div>

          {transactions && transactions.length > 0 ? (
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {new Date(transaction.transaction_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">
                        {transaction.description || 'No description'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {transaction.quickbooks_account_id}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </TableCell>
                      <TableCell>
                        {transaction.sync_status === 'mapped' ? (
                          <span className="text-green-600">Mapped</span>
                        ) : (
                          <span className="text-gray-500">Unmapped</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.sync_status !== 'mapped' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMapTransaction(transaction)}
                          >
                            Map to Asset
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">Mapped</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="mt-4 text-center py-8 text-muted-foreground">
              <p>No transactions synced yet.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() =>
                  selectedConnection && handleSyncTransactions(selectedConnection.id)
                }
                disabled={
                  !selectedConnection ||
                  syncingTransactionsId === selectedConnection.id
                }
              >
                {syncingTransactionsId === selectedConnection?.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Transactions
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Map Transaction Dialog */}
      <Dialog open={!!mappingTransaction} onOpenChange={(open) => !open && setMappingTransaction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Map Transaction to Asset</DialogTitle>
            <DialogDescription>
              Select an asset to map this QuickBooks transaction to.
            </DialogDescription>
          </DialogHeader>

          {mappingTransaction && (
            <MapTransactionForm
              transaction={mappingTransaction}
              assets={assets}
              onSubmit={handleMapSubmit}
              onCancel={() => setMappingTransaction(null)}
              isLoading={mapTransactionMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Map Transaction Form Component
function MapTransactionForm({
  transaction,
  assets,
  onSubmit,
  onCancel,
  isLoading,
}: {
  transaction: QuickBooksTransaction;
  assets: Asset[];
  onSubmit: (assetId: string, category?: string, unit?: number | null) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [unit, setUnit] = useState<number | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  useEffect(() => {
    if (selectedAssetId) {
      const asset = assets.find((a) => a.id === selectedAssetId);
      setSelectedAsset(asset || null);
    } else {
      setSelectedAsset(null);
    }
  }, [selectedAssetId, assets]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedAssetId) {
      onSubmit(selectedAssetId, category || undefined, unit);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label>Transaction Details</Label>
        <div className="text-sm text-muted-foreground space-y-1">
          <p><strong>Date:</strong> {new Date(transaction.transaction_date).toLocaleDateString()}</p>
          <p><strong>Description:</strong> {transaction.description || 'No description'}</p>
          <p><strong>Amount:</strong> {formatCurrency(transaction.amount, transaction.currency)}</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="asset">Select Asset *</Label>
        <Select value={selectedAssetId} onValueChange={setSelectedAssetId} required>
          <SelectTrigger id="asset">
            <SelectValue placeholder="Choose an asset" />
          </SelectTrigger>
          <SelectContent>
            {assets.map((asset) => (
              <SelectItem key={asset.id} value={asset.id}>
                {asset.name} {asset.address ? `- ${asset.address}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedAsset?.units && selectedAsset.units > 1 && (
        <div className="space-y-2">
          <Label htmlFor="unit">Unit Number (Optional)</Label>
          <Select
            value={unit?.toString() || ''}
            onValueChange={(value) => setUnit(value ? Number.parseInt(value, 10) : null)}
          >
            <SelectTrigger id="unit">
              <SelectValue placeholder="Select unit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">None</SelectItem>
              {Array.from({ length: selectedAsset.units }, (_, i) => i + 1).map((unitNum) => (
                <SelectItem key={unitNum} value={unitNum.toString()}>
                  Unit {unitNum}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="category">Category (Optional)</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger id="category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">Default</SelectItem>
            <SelectItem value="RENT_INCOME">Rent Income</SelectItem>
            <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
            <SelectItem value="REPAIRS">Repairs</SelectItem>
            <SelectItem value="UTILITIES">Utilities</SelectItem>
            <SelectItem value="INSURANCE">Insurance</SelectItem>
            <SelectItem value="PROPERTY_TAX">Property Tax</SelectItem>
            <SelectItem value="OTHER_EXPENSE">Other Expense</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={!selectedAssetId || isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Mapping...
            </>
          ) : (
            'Map Transaction'
          )}
        </Button>
      </div>
    </form>
  );
}

export default function QuickBooksPage() {
  return (
    <QuickBooksFeature>
      <QuickBooksPageContent />
    </QuickBooksFeature>
  );
}

